import { useEffect, useState, useCallback } from "react";
import { InfoHint } from "../components/InfoHint";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { highlightMatch } from "../lib/highlightMatch";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { StarRating } from "../components/StarRating";
import { CategorieManager } from "../components/CategorieManager";
import { toast } from "sonner";
import { Wrench, Plus, Pencil, Trash2, Phone, Mail, Star, Upload, Download, CheckCircle2, FileSpreadsheet, MapPin, KeyRound, Tags, Ban, FolderArchive, Smartphone, MessageCircle, ShieldCheck, Send, Loader2, Users2, Share2, Rows3, LayoutGrid, Eye } from "lucide-react";
import { CestinoButton } from "../components/CestinoButton";
import { FornitoreFascicoloDialog } from "../components/FornitoreFascicolo";
import { MultiTenantBadge } from "../components/MultiTenantBadge";

const CATS_DEFAULT = ["Elettrica", "Idraulica", "Muraria", "Serramenti/Fabbro", "Ascensore", "Riscaldamento/Caldaia", "Tetto/Lattoneria", "Pulizie", "Giardinaggio", "Altro"];
const EMPTY = { ragione_sociale: "", categorie: [], settore: "", referente: "", email: "", pec: "", telefono: "", telefono_fisso: "", cellulare: "", indirizzo: "", citta: "", cap: "", provincia: "", codice_fiscale: "", partita_iva: "", note: "", regime_fiscale: "", iban: "", codice_sdi: "", pct_cpa: 4.0, pct_iva: 22.0, ritenuta_acconto: false };

export default function Fornitori() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === "super_admin" || user?.tenant_id === "studio";
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [error, setError] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importPreview, setImportPreview] = useState(null); // Iter122fk · dry-run
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState("");
  const [total, setTotal] = useState(0);
  const [credInfo, setCredInfo] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkList, setBulkList] = useState(null);
  const [bulkSel, setBulkSel] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [stato, setStato] = useState(null);
  const [invioCred, setInvioCred] = useState(false);
  const [solleciting, setSolleciting] = useState(false);
  const [statoFiltro, setStatoFiltro] = useState("tutti");
  // Iter122di · toggle "Solo condivisi con altri Studi" (fornitori multi-tenant)
  const [soloCondivisi, setSoloCondivisi] = useState(false);
  // Iter122bc · filtro categoria (opzionalmente da URL ?categoria=X, es. da Onboarding studi)
  const [catFiltro, setCatFiltro] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("categoria") || "";
  });
  const [cats, setCats] = useState(CATS_DEFAULT);
  const [catOpen, setCatOpen] = useState(false);
  // Iter122fh · vista minimal (alternativa a estesa) · selezione per export mirato
  const [viewMode, setViewMode] = useState("esteso"); // "esteso" | "minimal"
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [groupBy, setGroupBy] = useState("nessuno"); // "nessuno" | "categoria"
  const [sortDir, setSortDir] = useState("az"); // "az" | "za"
  const [fascicoloForn, setFascicoloForn] = useState(null);
  // Iter113 · dialog per inviare al fornitore il riepilogo attività attive (manutenzioni + preventivi)
  const [riepForn, setRiepForn] = useState(null);
  const [riepOpts, setRiepOpts] = useState({ manutenzioni: true, preventivi: true });
  const [riepInv, setRiepInv] = useState(false);

  const inviaRiepilogo = async () => {
    if (!riepForn) return;
    if (!riepOpts.manutenzioni && !riepOpts.preventivi) {
      toast.error("Seleziona almeno una tipologia di riepilogo");
      return;
    }
    setRiepInv(true);
    try {
      const { data } = await api.post(`/fornitori/${riepForn.id}/invia-riepilogo`, null, {
        params: { include_manutenzioni: riepOpts.manutenzioni, include_preventivi: riepOpts.preventivi },
      });
      const parts = [];
      if (data.ticket_count) parts.push(`${data.ticket_count} manutenzion${data.ticket_count === 1 ? "e" : "i"}`);
      if (data.preventivo_count) parts.push(`${data.preventivo_count} preventiv${data.preventivo_count === 1 ? "o" : "i"}`);
      toast.success(`Riepilogo inviato a ${data.sent_to} (${parts.join(" + ")})`);
      setRiepForn(null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRiepInv(false); }
  };

  // Iter122fi · Anteprima PDF del riepilogo (senza invio email): apre il PDF in nuova tab.
  const anteprimaRiepilogo = async () => {
    if (!riepForn) return;
    if (!riepOpts.manutenzioni && !riepOpts.preventivi) {
      toast.error("Seleziona almeno una tipologia di riepilogo");
      return;
    }
    try {
      const res = await api.get(`/fornitori/${riepForn.id}/riepilogo-pdf`, {
        params: { include_manutenzioni: riepOpts.manutenzioni, include_preventivi: riepOpts.preventivi },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Nessuna attività attiva da riepilogare");
    }
  };

  const loadCats = () => api.get("/meta").then((r) => setCats(r.data.categorie || CATS_DEFAULT)).catch(() => {});
  useEffect(() => { loadCats(); }, []);

  const matchStato = (f) => {
    if (statoFiltro === "rilasciati") return f.has_account;
    if (statoFiltro === "attivati") return f.account_attivo;
    if (statoFiltro === "da_attivare") return f.has_account && !f.account_attivo;
    if (statoFiltro === "senza_account") return !f.has_account;
    return true;
  };
  const displayList = list.filter(matchStato).filter((f) => !soloCondivisi || (f.altri_tenant_count || 0) > 0);
  const toggleStato = (v) => setStatoFiltro((s) => (s === v ? "tutti" : v));

  const loadStato = useCallback(() => {
    api.get("/stato-attivazioni").then((r) => setStato(r.data.fornitori)).catch(() => {});
  }, []);

  const load = useCallback((term = "", stato = "tutti", cat = "") => {
    const params = { search: term || undefined, limit: 5000 };
    if (stato && stato !== "tutti") params.stato_account = stato;
    if (cat) params.categoria = cat;
    api.get("/fornitori", { params })
      .then((r) => { setList(r.data.fornitori); setTotal(r.data.total); })
      .catch(() => {});
  }, []);

  useEffect(() => { loadStato(); }, [loadStato]);
  useEffect(() => { api.get("/settings").then((r) => setInvioCred(!!r.data.invio_credenziali)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(search, statoFiltro, catFiltro), 300);
    return () => clearTimeout(t);
  }, [search, statoFiltro, catFiltro, load]);

  const sollecitaFornitori = async () => {
    setSolleciting(true);
    try {
      const { data } = await api.post("/stato-attivazioni/sollecita", { target: "fornitori" });
      toast.success(data.inviati > 0
        ? `${data.inviati} promemoria di attivazione inviati ai fornitori`
        : "Nessun fornitore da sollecitare");
      loadStato();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setSolleciting(false); }
  };

  const downloadTemplate = async () => {
    const res = await api.get("/fornitori/template", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "modello_fornitori.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  // Iter122fc · Esporta l'anagrafica fornitori per condivisione con altri Studi.
  // Riservato allo Studio Izzo (backend restituisce 403 per altri tenant).
  // Il file è già ri-importabile con il pulsante "Importa Excel".
  // Iter122fh · se `idsSubset` è passato (Set), esporta SOLO quei fornitori.
  const downloadExport = async (format = "xlsx", idsSubset = null) => {
    try {
      const params = { format };
      if (idsSubset && idsSubset.size > 0) {
        params.ids = Array.from(idsSubset).join(",");
      }
      const res = await api.get("/fornitori/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const suffix = idsSubset ? `_selezione_${idsSubset.size}` : "";
      a.download = format === "csv" ? `fornitori_ginius${suffix}.csv` : `fornitori_ginius${suffix}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
      toast.success(`Anagrafica esportata (${format.toUpperCase()}${idsSubset ? ` · ${idsSubset.size} selezionati` : ""})`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Impossibile esportare l'anagrafica");
    }
  };

  const downloadCredenziali = async () => {
    try {
      const res = await api.get("/fornitori/credenziali", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "credenziali_fornitori.xlsx"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 15000);
    } catch { toast.error("Impossibile scaricare le credenziali"); }
  };

  const doImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    try {
      // Iter122fk · Step 1 · anteprima dry-run per mostrare cosa verrà creato/aggiornato/saltato
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post("/fornitori/import?dry_run=true&update_existing=false", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview({ ...data, file: importFile });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setImporting(false); }
  };

  // Iter122fk · Step 2 · conferma: applica realmente (con o senza aggiornamento esistenti)
  const confirmImport = async (updateExisting) => {
    if (!importPreview?.file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importPreview.file);
      const { data } = await api.post(
        `/fornitori/import?dry_run=false&update_existing=${updateExisting ? "true" : "false"}`,
        fd, { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportResult(data);
      setImportPreview(null);
      load();
      const bits = [`${data.created} creati`];
      if (data.updated) bits.push(`${data.updated} aggiornati`);
      if ((data.skipped || []).length) bits.push(`${data.skipped.length} saltati`);
      toast.success(bits.join(" · "));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setImporting(false); }
  };

  const openNew = () => { setForm(EMPTY); setEditId(null); setError(""); setOpen(true); };
  const openEdit = (f) => { setForm({ ...EMPTY, ...f }); setEditId(f.id); setError(""); setOpen(true); };

  const toggleCat = (c) =>
    setForm((f) => ({ ...f, categorie: f.categorie.includes(c) ? f.categorie.filter((x) => x !== c) : [...f.categorie, c] }));

  const save = async () => {
    setError("");
    try {
      if (editId) await api.put(`/fornitori/${editId}`, form);
      else await api.post("/fornitori", form);
      setOpen(false);
      load(search, statoFiltro);
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/fornitori/${id}`); load(search, statoFiltro); };

  const creaAccount = async (f) => {
    if (!f.email) { toast.error("Aggiungi prima un'email al fornitore"); return; }
    // Iter122dg · pre-check cross-tenant: se l'email risulta su altri Studi, avvisa l'admin
    // (non blocca — è multi-account supportato).
    try {
      const { data: chk } = await api.get(`/me/anagrafiche/email-check?email=${encodeURIComponent(f.email)}`);
      if (chk?.other_tenants > 0) {
        toast.info(`Nota: questa email risulta già registrata presso ${chk.other_tenants} altro Studio. Verrà creato un account autonomo, l'utente sceglierà lo Studio al login.`, { duration: 6000 });
      }
    } catch { /* non blocca */ }
    try {
      const { data } = await api.post(`/fornitori/${f.id}/account`, {});
      setCredInfo({ nome: f.ragione_sociale, ...data.credenziali, email: data.email, email_inviata: data.email_inviata });
      load(search, statoFiltro); loadStato();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const inviaCredenziali = async (f) => {
    try {
      const { data } = await api.post(`/fornitori/${f.id}/invia-credenziali`);
      toast.success(`Credenziali inviate a ${data.email}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const disattivaAccount = async (f) => {
    if (!window.confirm(`Disattivare l'account di "${f.ragione_sociale}"?\n\nL'accesso attuale verrà revocato e l'account tornerà "da attivare" con un nuovo codice, così da poter reinviare nuove credenziali.`)) return;
    try {
      const { data } = await api.post(`/fornitori/${f.id}/disattiva`);
      toast.success(data.email_inviata ? `Account disattivato · nuove credenziali inviate a ${data.email}` : "Account disattivato · ora è 'da attivare', puoi inviare nuove credenziali");
      load(search, statoFiltro); loadStato();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const riemettiCredenziali = async (f) => {
    if (!window.confirm(`Riemettere le credenziali di accesso per "${f.ragione_sociale}"?\n\nVerrà generato un NUOVO codice di attivazione, la password attuale sarà azzerata e verrà inviata subito una nuova email di attivazione.`)) return;
    try {
      const { data } = await api.post(`/fornitori/${f.id}/riemetti-credenziali`);
      setCredInfo({ nome: f.ragione_sociale, login_id: data.login_id, activation_code: data.activation_code, email: data.email, email_inviata: data.email_inviata });
      load(search, statoFiltro); loadStato();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleDisabilita = async (f) => {
    const nuovo = !f.account_disabilitato;
    if (nuovo && !window.confirm(`Disabilitare l'account di "${f.ragione_sociale}"? Non potrà più accedere finché non lo riabiliti.`)) return;
    try {
      await api.post(`/fornitori/${f.id}/disabilita`, { disabilitato: nuovo });
      toast.success(nuovo ? "Account disabilitato" : "Account riabilitato");
      load(search, statoFiltro); loadStato();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const assegnaCategoria = async (f, cat) => {
    if (!cat) return;
    try { await api.post(`/fornitori/${f.id}/categoria`, { categoria: cat }); toast.success(`Categoria "${cat}" assegnata`); load(search, statoFiltro); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openBulk = async () => {
    setBulkOpen(true); setBulkList(null); setBulkResult(null);
    try {
      const { data } = await api.get("/fornitori", { params: { limit: 5000 } });
      const eligibili = (data.fornitori || []).filter((f) => f.email && !f.account_attivo);
      setBulkList(eligibili);
      const sel = {}; eligibili.forEach((f) => { sel[f.id] = true; });
      setBulkSel(sel);
    } catch { toast.error("Errore nel caricamento fornitori"); }
  };

  const inviaBulk = async () => {
    const ids = Object.keys(bulkSel).filter((id) => bulkSel[id]);
    if (!ids.length) { toast.error("Seleziona almeno un fornitore"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/fornitori/attiva-selezione", { ids });
      setBulkResult(data);
      load(search, statoFiltro); loadStato();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBulkBusy(false);
  };

  return (
    <div className="max-w-6xl">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex items-center gap-2">
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Account Fornitori</h1>
          <InfoHint section="fornitori" />
        </div>
        <div className="grid grid-cols-2 gap-2 w-full sm:w-auto sm:flex sm:flex-wrap">
          {/* Iter122fh · Toggle vista estesa / minimal — full-width su mobile per leggibilità */}
          <div className="col-span-2 sm:col-span-1 inline-flex h-10 rounded-lg overflow-hidden border-2 border-brand/40 w-full sm:w-auto" role="tablist">
            <button type="button" data-testid="forn-view-esteso"
              onClick={() => setViewMode("esteso")}
              className={`flex-1 sm:flex-none px-3 text-sm font-bold inline-flex items-center justify-center gap-1.5 ${viewMode === "esteso" ? "bg-brand text-white" : "bg-white text-brand hover:bg-brand/5"}`}>
              <LayoutGrid className="w-4 h-4" /> Estesa
            </button>
            <button type="button" data-testid="forn-view-minimal"
              onClick={() => setViewMode("minimal")}
              className={`flex-1 sm:flex-none px-3 text-sm font-bold inline-flex items-center justify-center gap-1.5 ${viewMode === "minimal" ? "bg-brand text-white" : "bg-white text-brand hover:bg-brand/5"}`}>
              <Rows3 className="w-4 h-4" /> Minimal
            </button>
          </div>
          <Button data-testid="download-forn-template-button" variant="outline" className="h-10 text-sm w-full sm:w-auto justify-center" onClick={downloadTemplate}>
            <Download className="w-4 h-4 mr-1.5" /> Modello Excel
          </Button>
          {isPlatformAdmin && (
            <>
              <Button data-testid="export-forn-xlsx-button" variant="outline" className="h-10 text-sm border-brand/40 text-brand w-full sm:w-auto justify-center" onClick={() => downloadExport("xlsx")}
                title="Esporta l'anagrafica fornitori in Excel — condividile con altri Studi (ri-importabile).">
                <Share2 className="w-4 h-4 mr-1.5" /> Esporta Excel
              </Button>
              <Button data-testid="export-forn-csv-button" variant="outline" className="h-10 text-sm border-brand/40 text-brand w-full sm:w-auto justify-center" onClick={() => downloadExport("csv")}
                title="Esporta l'anagrafica fornitori in CSV (UTF-8 con BOM, separatore ;).">
                <Share2 className="w-4 h-4 mr-1.5" /> Esporta CSV
              </Button>
            </>
          )}
          <Button data-testid="open-forn-import-button" variant="outline" className="h-10 text-sm w-full sm:w-auto justify-center" onClick={() => { setImportOpen(true); setImportResult(null); setImportFile(null); }}>
            <Upload className="w-4 h-4 mr-1.5" /> Importa Excel
          </Button>
          <Button data-testid="download-forn-credenziali-button" variant="outline" className="h-10 text-sm w-full sm:w-auto justify-center" onClick={downloadCredenziali}>
            <KeyRound className="w-4 h-4 mr-1.5" /> Credenziali accesso
          </Button>
          <Button data-testid="bulk-credenziali-button" variant="outline" className="h-10 text-sm border-brand/30 text-brand w-full sm:w-auto justify-center" onClick={openBulk}>
            <KeyRound className="w-4 h-4 mr-1.5" /> Invia credenziali
          </Button>
          <Button data-testid="add-fornitore-button" onClick={openNew} className="col-span-2 sm:col-span-1 h-10 px-4 text-sm font-bold bg-brand hover:bg-brand-dark w-full sm:w-auto justify-center">
            <Plus className="w-4 h-4 mr-1.5" /> Aggiungi
          </Button>
          <div className="col-span-2 sm:col-span-1 flex justify-end sm:justify-start w-full sm:w-auto">
            <CestinoButton entity="fornitori" onChange={() => { load(search, statoFiltro); loadStato(); }} />
          </div>
        </div>
      </div>

      <div className="mb-4">
        <Input data-testid="fornitori-search" value={search} onChange={(e) => setSearch(e.target.value)}
          className="h-11 text-base max-w-md" placeholder="Cerca per nome, settore, città o referente..." />
        <p className="text-sm text-slate-500 mt-2">
          {search
            ? `${list.length} risultati su ${total} fornitori`
            : statoFiltro !== "tutti"
              ? `${total} fornitori in questo stato${total > list.length ? ` (mostrati i primi ${list.length})` : ""}`
              : `${total} fornitori in anagrafica${total > list.length ? ` (mostrati i primi ${list.length}, usa la ricerca)` : ""}`}
        </p>
        {/* Iter122bc · badge del filtro categoria attivo (da URL o da Onboarding studi) */}
        {catFiltro && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-brand/10 border-2 border-brand/30 text-sm" data-testid="cat-filter-badge">
            <Tags className="w-4 h-4 text-brand" />
            <span className="font-semibold text-brand">Filtro attivo: {catFiltro}</span>
            <button type="button" data-testid="cat-filter-clear" onClick={() => setCatFiltro("")} className="text-brand hover:text-brand-dark font-bold ml-1">✕</button>
          </div>
        )}
        {/* Iter122di · toggle "Solo condivisi con altri Studi" */}
        {(() => {
          const nCondivisi = list.filter((f) => (f.altri_tenant_count || 0) > 0).length;
          if (nCondivisi === 0 && !soloCondivisi) return null;
          return (
            <div className="mt-3">
              <button
                type="button"
                data-testid="filter-condivisi-toggle"
                onClick={() => setSoloCondivisi((v) => !v)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border-2 text-sm font-semibold transition-colors ${
                  soloCondivisi ? "bg-sky-600 border-sky-600 text-white" : "bg-white border-sky-300 text-sky-800 hover:bg-sky-50"
                }`}
                title="Mostra solo fornitori con account attivo anche presso altri Studi Ginius"
              >
                <Users2 className="w-4 h-4" /> Solo condivisi ({nCondivisi})
              </button>
            </div>
          );
        })()}
      </div>

      {stato && (stato.totale_account > 0 || stato.senza_account > 0) && (
        <Card data-testid="forn-stato-attivazioni-card" className="p-4 border-2 mb-4">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-6 h-6 text-brand" />
              <h3 className="font-display text-xl font-bold">Stato attivazioni fornitori</h3>
            </div>
            <Button data-testid="forn-sollecita-attivazioni-btn" variant="outline"
              disabled={!invioCred || solleciting || stato.da_attivare === 0}
              className="h-11 border-amber-400 text-amber-800" onClick={sollecitaFornitori}
              title={!invioCred ? "Attiva prima l'invio credenziali (Impostazioni)" : "Invia un sollecito ai fornitori non attivati"}>
              <Mail className="w-5 h-5 mr-2" /> {solleciting ? "Invio..." : `Sollecita non attivi (${stato.da_attivare})`}
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <button type="button" data-testid="forn-filter-rilasciati" onClick={() => toggleStato("rilasciati")}
              className={`min-w-0 text-center p-3 rounded-lg bg-slate-50 border transition-all ${statoFiltro === "rilasciati" ? "ring-2 ring-brand border-brand" : "hover:border-slate-400"}`}>
              <div data-testid="forn-stat-totale" className="text-3xl font-extrabold">{stato.totale_account}</div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 break-words leading-tight">Account rilasciati</div>
            </button>
            <button type="button" data-testid="forn-filter-attivati" onClick={() => toggleStato("attivati")}
              className={`min-w-0 text-center p-3 rounded-lg bg-green-50 border border-green-200 transition-all ${statoFiltro === "attivati" ? "ring-2 ring-green-600" : "hover:border-green-400"}`}>
              <div data-testid="forn-stat-attivati" className="text-3xl font-extrabold text-green-700">{stato.attivati}</div>
              <div className="text-xs font-semibold uppercase tracking-wide text-green-700 break-words leading-tight">Account attivati</div>
            </button>
            <button type="button" data-testid="forn-filter-da-attivare" onClick={() => toggleStato("da_attivare")}
              className={`min-w-0 text-center p-3 rounded-lg bg-amber-50 border border-amber-200 transition-all ${statoFiltro === "da_attivare" ? "ring-2 ring-amber-600" : "hover:border-amber-400"}`}>
              <div data-testid="forn-stat-da-attivare" className="text-3xl font-extrabold text-amber-700">{stato.da_attivare}</div>
              <div className="text-xs font-semibold uppercase tracking-wide text-amber-700 break-words leading-tight">Account da attivare</div>
            </button>
            <button type="button" data-testid="forn-filter-senza-account" onClick={() => toggleStato("senza_account")}
              className={`min-w-0 text-center p-3 rounded-lg bg-slate-50 border transition-all ${statoFiltro === "senza_account" ? "ring-2 ring-slate-500 border-slate-500" : "hover:border-slate-400"}`}>
              <div data-testid="forn-stat-senza-account" className="text-3xl font-extrabold text-slate-400">{stato.senza_account}</div>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 break-words leading-tight">Senza account</div>
            </button>
          </div>
          <div className="text-xs text-slate-400" data-testid="forn-ultimo-sollecito">
            {stato.ultimo_sollecito
              ? `Ultimo sollecito: ${new Date(stato.ultimo_sollecito).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}`
              : "Nessun sollecito inviato finora"}
            {!invioCred && <span className="ml-2 text-amber-600 font-semibold">· Invio credenziali disattivato (Impostazioni)</span>}
          </div>
        </Card>
      )}

      {statoFiltro !== "tutti" && (
        <div className="flex items-center gap-3 mb-3 text-sm" data-testid="forn-stato-filtro-banner">
          <span className="font-semibold text-slate-600">Filtro attivo: {({ rilasciati: "Account rilasciati", attivati: "Account attivati", da_attivare: "Account da attivare", senza_account: "Senza account" }[statoFiltro] || statoFiltro)} ({displayList.length})</span>
          <button data-testid="forn-reset-stato-filtro" onClick={() => setStatoFiltro("tutti")} className="text-brand font-semibold hover:underline">Mostra tutti</button>
        </div>
      )}
      {/* Iter122fh · toolbar della vista minimal con selezione e raggruppamento */}
      {viewMode === "minimal" && (
        <MinimalToolbar
          list={displayList} selectedIds={selectedIds} setSelectedIds={setSelectedIds}
          groupBy={groupBy} setGroupBy={setGroupBy} sortDir={sortDir} setSortDir={setSortDir}
          isPlatformAdmin={isPlatformAdmin} onExport={(fmt) => downloadExport(fmt, selectedIds)}
        />
      )}
      {viewMode === "minimal" ? (
        <MinimalTable list={displayList} selectedIds={selectedIds} setSelectedIds={setSelectedIds}
          groupBy={groupBy} sortDir={sortDir} search={search} />
      ) : (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {displayList.length === 0 && (
          <Card className="p-8 text-center text-slate-500 sm:col-span-2 lg:col-span-3">Nessun fornitore corrisponde al filtro selezionato.</Card>
        )}
        {displayList.map((f) => (
          <Card key={f.id} data-testid={`fornitore-card-${f.ragione_sociale}`} className="p-3 border-2 min-w-0 hover:border-[#C98AA0] transition-colors">
            <div className="flex items-start gap-2 mb-1.5 min-w-0">
              <div className="bg-orange-100 text-orange-700 rounded-lg p-1 shrink-0"><Wrench className="w-4 h-4" /></div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold text-sm sm:text-base leading-tight break-words">{highlightMatch(f.ragione_sociale, search)}</h3>
                {f.referente && <div className="text-xs text-slate-500">{highlightMatch(f.referente, search)}</div>}
                {f.altri_tenant_count > 0 && (
                  <div className="mt-1"><MultiTenantBadge count={f.altri_tenant_count} testid={`multitenant-badge-${f.ragione_sociale}`} /></div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mb-1.5 items-center">
              {f.settore && <span className="px-2 py-0.5 bg-orange-50 text-orange-800 rounded text-xs font-semibold">{highlightMatch(f.settore, search)}</span>}
              {f.categorie?.map((c) => <span key={c} className="px-2 py-0.5 bg-slate-100 rounded text-xs font-semibold">{c}</span>)}
              {!f.categorie?.length && (
                <select data-testid={`assegna-categoria-${f.ragione_sociale}`} defaultValue=""
                  onChange={(e) => { assegnaCategoria(f, e.target.value); e.target.value = ""; }}
                  className="h-7 max-w-full text-xs border-2 border-dashed border-amber-300 rounded-lg px-2 bg-amber-50 text-amber-800 font-semibold">
                  <option value="">+ Assegna categoria</option>
                  {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              )}
            </div>
            <div className="text-xs text-slate-600 space-y-0.5 mb-1.5">
              {(f.indirizzo || f.cap || f.citta || f.provincia) && (
                <div className="flex items-start gap-1.5 truncate" data-testid={`fornitore-indirizzo-${f.ragione_sociale}`}>
                  <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span className="truncate">{highlightMatch([f.indirizzo, [f.cap, f.citta].filter(Boolean).join(" "), f.provincia && `(${f.provincia})`].filter(Boolean).join(", "), search)}</span>
                </div>
              )}
              {(f.telefono_fisso || (!f.cellulare && f.telefono)) && (
                <div className="flex items-center gap-1.5" data-testid={`fornitore-fisso-${f.ragione_sociale}`}>
                  <Phone className="w-3.5 h-3.5 shrink-0" />
                  <a href={`tel:${(f.telefono_fisso || f.telefono || '').replace(/\s+/g, '')}`}
                    className="hover:text-brand hover:underline">{f.telefono_fisso || f.telefono}</a>
                </div>
              )}
              {f.cellulare && (
                <div className="flex items-center gap-1.5" data-testid={`fornitore-cellulare-${f.ragione_sociale}`}>
                  <Smartphone className="w-3.5 h-3.5 shrink-0" />
                  <a href={`tel:${(f.cellulare || '').replace(/\s+/g, '')}`}
                    className="hover:text-brand hover:underline">{f.cellulare}</a>
                  <a href={`https://wa.me/${(f.cellulare || '').replace(/[^\d]/g, '').replace(/^0+/, '39')}`}
                    target="_blank" rel="noopener noreferrer" title="Apri chat WhatsApp"
                    data-testid={`fornitore-whatsapp-${f.ragione_sociale}`}
                    className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#25D366]/15 text-[#128C7E] hover:bg-[#25D366]/25 font-semibold">
                    <MessageCircle className="w-3 h-3" /> WhatsApp
                  </a>
                </div>
              )}
              {f.email && <div className="flex items-start gap-1.5 break-all"><Mail className="w-3.5 h-3.5 mt-0.5 shrink-0" /> <span>{f.email}</span></div>}
              {f.pec && <div className="flex items-start gap-1.5 break-all" data-testid={`fornitore-pec-${f.ragione_sociale}`}><ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-brand" /> <span className="font-mono">{f.pec}</span></div>}
            </div>
            <div className="flex items-center gap-2 mb-2">
              {f.rating_medio ? (
                <>
                  <StarRating value={Math.round(f.rating_medio)} readOnly size={4} />
                  <span className="text-xs font-bold">{f.rating_medio}</span>
                  <span className="text-xs text-slate-500">({f.num_valutazioni})</span>
                </>
              ) : <span className="text-xs text-slate-400 flex items-center gap-1"><Star className="w-3.5 h-3.5" /> Nessuna valutazione</span>}
            </div>
            <div className="mb-2 pt-2 border-t border-slate-100">
              {f.account_disabilitato && (
                <span data-testid={`forn-disabilitato-badge-${f.ragione_sociale}`} className="inline-flex items-center gap-1 mb-1.5 px-2 py-0.5 rounded bg-red-100 text-red-700 text-xs font-semibold">
                  <Ban className="w-3.5 h-3.5" /> Disabilitato
                </span>
              )}
              {f.account_attivo ? (
                <div className="flex items-center justify-between gap-2 min-w-0" data-testid={`forn-account-attivo-${f.ragione_sociale}`}>
                  <span className="flex items-center gap-1.5 text-green-700 text-xs font-semibold min-w-0">
                    <KeyRound className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">Account attivo{f.account_login_id && <span className="text-slate-400 font-normal"> · ID {f.account_login_id}</span>}</span>
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <Button data-testid={`forn-riemetti-${f.ragione_sociale}`} variant="outline" size="sm"
                      className="h-7 px-2 text-xs text-brand border-brand/30 hover:bg-[#FBEEF3]" title="Rigenera codice e reinvia email di attivazione" onClick={() => riemettiCredenziali(f)}>
                      Riemetti
                    </Button>
                    <Button data-testid={`forn-disattiva-${f.ragione_sociale}`} variant="outline" size="sm"
                      className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => disattivaAccount(f)}>
                      Disattiva
                    </Button>
                  </div>
                </div>
              ) : f.has_account ? (
                <div className="flex items-center justify-between gap-2 min-w-0">
                  <span className="text-amber-700 text-xs font-semibold flex items-center gap-1 min-w-0">
                    <KeyRound className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">Da attivare{f.account_login_id && ` · ${f.account_login_id}`}</span>
                  </span>
                  <Button data-testid={`forn-resend-${f.ragione_sociale}`} variant="outline" size="sm" className="h-7 px-2 text-xs shrink-0" onClick={() => inviaCredenziali(f)}>Invia</Button>
                </div>
              ) : (
                <Button data-testid={`forn-crea-account-${f.ragione_sociale}`} variant="outline" size="sm" className="h-8 w-full text-xs text-brand border-brand/30" disabled={!f.email} onClick={() => creaAccount(f)}>
                  <KeyRound className="w-4 h-4 mr-1" /> {f.email ? "Crea account di accesso" : "Aggiungi email per l'account"}
                </Button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid={`edit-fornitore-${f.ragione_sociale}`} variant="outline" size="sm" className="h-8 px-2 text-xs" onClick={() => openEdit(f)}>
                <Pencil className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Modifica</span>
              </Button>
              <Button data-testid={`forn-fascicolo-${f.ragione_sociale}`} variant="outline" size="sm" className="h-8 px-2 text-xs text-teal-700 border-teal-300"
                title="Fascicolo documentale · attività periodiche assegnate" onClick={() => setFascicoloForn(f)}>
                <FolderArchive className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Fascicolo</span>
              </Button>
              <Button data-testid={`forn-riepilogo-${f.ragione_sociale}`} variant="outline" size="sm" className="h-8 px-2 text-xs text-indigo-700 border-indigo-300"
                title={f.email ? "Invia via email il riepilogo attività in corso" : "Manca l'email del fornitore"}
                disabled={!f.email} onClick={() => { setRiepForn(f); setRiepOpts({ manutenzioni: true, preventivi: true }); }}>
                <Send className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Riepilogo</span>
              </Button>
              {f.has_account && (
                <Button data-testid={`forn-disabilita-${f.ragione_sociale}`} variant="outline" size="sm"
                  className={`h-8 px-2 text-xs ${f.account_disabilitato ? "text-green-700 border-green-300" : "text-red-700 border-red-300"}`}
                  title={f.account_disabilitato ? "Riabilita l'accesso" : "Disabilita l'accesso"} onClick={() => toggleDisabilita(f)}>
                  <Ban className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">{f.account_disabilitato ? "Riabilita" : "Disabilita"}</span>
                </Button>
              )}
              <Button data-testid={`delete-fornitore-${f.ragione_sociale}`} variant="outline" size="sm" className="h-8 px-2 text-red-700 border-red-300 ml-auto" onClick={() => remove(f.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl">{editId ? "Modifica" : "Nuovo"} fornitore</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Ragione sociale" required v={form.ragione_sociale} on={(v) => setForm({ ...form, ragione_sociale: v })} testid="forn-nome" />
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Categorie</Label>
                <button type="button" data-testid="forn-gestisci-categorie" onClick={() => setCatOpen(true)}
                  className="text-brand text-sm font-semibold inline-flex items-center gap-1 hover:underline">
                  <Tags className="w-4 h-4" /> Gestisci categorie
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-1">
                {cats.map((c) => (
                  <button key={c} type="button" data-testid={`forn-cat-${c}`} onClick={() => toggleCat(c)}
                    className={`px-3 py-1.5 rounded-full border-2 text-sm font-semibold ${form.categorie.includes(c) ? "border-brand bg-[#FBEEF3] text-brand" : "border-slate-200"}`}>
                    {c}
                  </button>
                ))}
              </div>
            </div>
            <Field label="Referente" v={form.referente} on={(v) => setForm({ ...form, referente: v })} />
            <Field label="Settore (descrizione libera)" v={form.settore} on={(v) => setForm({ ...form, settore: v })} />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Telefono fisso" v={form.telefono_fisso} on={(v) => setForm({ ...form, telefono_fisso: v })} testid="forn-tel-fisso" />
              <Field label="Cellulare (WhatsApp)" v={form.cellulare} on={(v) => setForm({ ...form, cellulare: v })} testid="forn-cellulare" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} testid="forn-email" />
              <Field label="PEC" v={form.pec} on={(v) => setForm({ ...form, pec: v })} testid="forn-pec" />
            </div>
            <Field label="Indirizzo e civico" v={form.indirizzo} on={(v) => setForm({ ...form, indirizzo: v })} testid="forn-indirizzo" />
            <div className="grid grid-cols-3 gap-3">
              <Field label="CAP" v={form.cap} on={(v) => setForm({ ...form, cap: v })} testid="forn-cap" />
              <div className="col-span-2"><Field label="Città" v={form.citta} on={(v) => setForm({ ...form, citta: v })} /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Provincia" v={form.provincia} on={(v) => setForm({ ...form, provincia: v })} testid="forn-provincia" />
              <Field label="Codice Fiscale" v={form.codice_fiscale} on={(v) => setForm({ ...form, codice_fiscale: (v || '').toUpperCase() })} testid="forn-cf" />
              <Field label="P.IVA" v={form.partita_iva} on={(v) => setForm({ ...form, partita_iva: v })} testid="forn-piva" />
            </div>
            {form.categorie?.includes("Avvocato-Studi Legali") && (
              <div className="border-2 border-sky-200 bg-sky-50/40 rounded-lg p-3 space-y-3">
                <div className="text-xs font-bold uppercase text-sky-800 flex items-center gap-2">
                  Dati fiscali per notule Credius (C4.1)
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs uppercase text-slate-500">Regime fiscale</label>
                    <select data-testid="forn-regime" value={form.regime_fiscale || ""}
                      onChange={(e) => setForm({ ...form, regime_fiscale: e.target.value })}
                      className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 bg-white mt-1">
                      <option value="">— Non specificato —</option>
                      <option>Ordinario</option>
                      <option>Forfettario</option>
                      <option>Semplificato</option>
                      <option>Minimi</option>
                    </select>
                  </div>
                  <Field label="IBAN" v={form.iban} on={(v) => setForm({ ...form, iban: (v || '').toUpperCase() })} testid="forn-iban" />
                  <Field label="Codice SDI" v={form.codice_sdi} on={(v) => setForm({ ...form, codice_sdi: (v || '').toUpperCase() })} testid="forn-sdi" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs uppercase text-slate-500">% CPA (default 4%)</label>
                    <input type="number" step="0.1" min="0" max="10" data-testid="forn-cpa"
                      value={form.pct_cpa ?? 4}
                      onChange={(e) => setForm({ ...form, pct_cpa: parseFloat(e.target.value) || 0 })}
                      className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 mt-1" />
                  </div>
                  <div>
                    <label className="text-xs uppercase text-slate-500">% IVA (0 per Forfettario/Minimi)</label>
                    <input type="number" step="0.1" min="0" max="30" data-testid="forn-iva"
                      value={form.pct_iva ?? 22}
                      onChange={(e) => setForm({ ...form, pct_iva: parseFloat(e.target.value) || 0 })}
                      className="h-11 w-full border-2 border-slate-300 rounded-lg px-3 mt-1" />
                  </div>
                  <label className="flex items-center gap-2 self-end p-2 rounded border border-slate-200 bg-white cursor-pointer">
                    <input type="checkbox" data-testid="forn-ra"
                      checked={!!form.ritenuta_acconto}
                      onChange={(e) => setForm({ ...form, ritenuta_acconto: e.target.checked })}
                      className="w-4 h-4 accent-sky-600" />
                    <span className="text-xs text-slate-700">Ritenuta d&apos;acconto 20%</span>
                  </label>
                </div>
              </div>
            )}
            {error && <div className="p-3 bg-red-50 text-red-800 rounded-lg text-base">{error}</div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="h-12">Annulla</Button>
            <Button data-testid="save-fornitore-button" onClick={save} className="h-12 bg-brand hover:bg-brand-dark">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl flex items-center gap-2"><FileSpreadsheet className="w-6 h-6" /> Importa fornitori da Excel</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-[#FBEEF3] border-2 border-[#F0D9E1] rounded-lg text-base text-slate-700">
              Colonne riconosciute: <b>Ragione sociale, Categorie, Referente, Telefono, Email, Indirizzo, Note</b>.
              Obbligatoria solo <b>Ragione sociale</b>. Più categorie separate da virgola o punto e virgola (devono corrispondere a quelle ammesse). I nomi già presenti vengono saltati. Formati: .xls e .xlsx.
              <Button variant="link" className="px-1 h-auto text-brand" onClick={downloadTemplate}>Scarica il modello</Button>
            </div>
            <Input data-testid="forn-import-file-input" type="file" accept=".xls,.xlsx" className="h-12"
              onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportPreview(null); setImportResult(null); }} />
            {/* Iter122fk · Anteprima dry-run: mostra cosa verrà creato/aggiornato/saltato */}
            {importPreview && (
              <div className="space-y-2" data-testid="forn-import-preview">
                <div className="p-3 bg-slate-50 border-2 border-slate-200 rounded-lg text-sm">
                  <div className="font-bold text-slate-800 mb-1">Anteprima import:</div>
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-green-100 border border-green-300 rounded p-2">
                      <div className="text-2xl font-bold text-green-700">{importPreview.will_create}</div>
                      <div className="text-xs text-green-700 font-semibold">da creare</div>
                    </div>
                    <div className="bg-blue-100 border border-blue-300 rounded p-2">
                      <div className="text-2xl font-bold text-blue-700">{importPreview.will_update}</div>
                      <div className="text-xs text-blue-700 font-semibold">aggiornabili*</div>
                    </div>
                    <div className="bg-amber-100 border border-amber-300 rounded p-2">
                      <div className="text-2xl font-bold text-amber-700">{importPreview.will_skip}</div>
                      <div className="text-xs text-amber-700 font-semibold">già presenti</div>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 mt-2">*match su ragione sociale / codice fiscale / P.IVA — aggiornati solo se scegli l&apos;opzione qui sotto.</div>
                  {importPreview.create_sample?.length > 0 && (
                    <div className="mt-2 text-xs"><b>Da creare:</b> {importPreview.create_sample.join(", ")}{importPreview.will_create > 10 ? " …" : ""}</div>
                  )}
                  {importPreview.update_sample?.length > 0 && (
                    <div className="mt-1 text-xs"><b>Aggiornabili:</b> {importPreview.update_sample.map((u) => `${u.nome} [${u.matched_by}] (${u.campi.join(", ")})`).join(" · ")}{importPreview.will_update > 10 ? " …" : ""}</div>
                  )}
                </div>
              </div>
            )}
            {importResult && (
              <div className="space-y-2">
                <div className="p-3 bg-green-50 border-2 border-green-200 rounded-lg text-green-800 font-semibold flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5" /> {importResult.created} creati · {importResult.updated || 0} aggiornati · {(importResult.skipped || []).length} saltati
                </div>
                {(importResult.skipped || []).length > 0 && (
                  <div className="border-2 border-amber-200 rounded-lg p-3 bg-amber-50 text-sm text-amber-900">
                    <b>Saltati: {importResult.skipped.length}</b>
                    <div className="max-h-28 overflow-y-auto mt-1">{importResult.skipped.join(", ")}</div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-wrap">
            <Button variant="outline" onClick={() => { setImportOpen(false); setImportPreview(null); }} className="h-12">Chiudi</Button>
            {!importPreview ? (
              <Button data-testid="do-forn-import-button" onClick={doImport} disabled={!importFile || importing} className="h-12 bg-brand hover:bg-brand-dark">
                {importing ? "Analisi..." : "Analizza file"}
              </Button>
            ) : (
              <>
                <Button data-testid="confirm-import-crea-only" onClick={() => confirmImport(false)}
                  disabled={importing} variant="outline" className="h-12 border-green-500 text-green-700">
                  {importing ? "..." : `Crea solo (${importPreview.will_create})`}
                </Button>
                {importPreview.will_update > 0 && (
                  <Button data-testid="confirm-import-crea-e-aggiorna" onClick={() => confirmImport(true)}
                    disabled={importing} className="h-12 bg-brand hover:bg-brand-dark">
                    {importing ? "..." : `Crea + Aggiorna (${importPreview.will_create + importPreview.will_update})`}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!credInfo} onOpenChange={(o) => !o && setCredInfo(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-2xl flex items-center gap-2"><KeyRound className="w-6 h-6 text-brand" /> Account creato</DialogTitle></DialogHeader>
          {credInfo && (
            <div className="space-y-3">
              <p className="text-base text-slate-600">Comunica al fornitore <b>{credInfo.nome}</b> queste credenziali per la prima attivazione sulla pagina <b>/attiva</b>:</p>
              <div className="rounded-lg bg-slate-50 border p-4 space-y-2 text-base">
                <div className="flex justify-between gap-3"><span className="text-slate-500">ID di accesso</span><b className="font-mono" data-testid="forn-cred-login">{credInfo.login_id}</b></div>
                <div className="flex justify-between gap-3"><span className="text-slate-500">Codice attivazione</span><b className="font-mono" data-testid="forn-cred-code">{credInfo.activation_code}</b></div>
              </div>
              <p className="text-sm text-slate-500">{credInfo.email_inviata ? `Email con le istruzioni inviata a ${credInfo.email}.` : "Invio email disattivato (Impostazioni): comunica tu le credenziali al fornitore (telefono/WhatsApp/email)."}</p>
            </div>
          )}
          <DialogFooter><Button onClick={() => setCredInfo(null)} className="h-12 bg-brand hover:bg-brand-dark">Ho capito</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="text-2xl flex items-center gap-2"><KeyRound className="w-6 h-6 text-brand" /> Invia credenziali ai fornitori</DialogTitle></DialogHeader>
          {bulkResult ? (
            <div className="space-y-4" data-testid="bulk-result">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                  <div className="text-2xl font-extrabold text-green-700" data-testid="bulk-res-creati">{bulkResult.creati}</div>
                  <div className="text-xs font-semibold uppercase text-green-700">Account creati</div>
                </div>
                <div className="rounded-lg bg-slate-50 border p-3">
                  <div className="text-2xl font-extrabold text-slate-600" data-testid="bulk-res-gia-attivi">{bulkResult.gia_attivi}</div>
                  <div className="text-xs font-semibold uppercase text-slate-500">Già attivi</div>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <div className="text-2xl font-extrabold text-amber-700" data-testid="bulk-res-saltati">{(bulkResult.duplicati || 0) + (bulkResult.senza_email || 0)}</div>
                  <div className="text-xs font-semibold uppercase text-amber-700">Saltati</div>
                </div>
              </div>
              {bulkResult.invio_attivo
                ? <p className="text-sm text-slate-500">{bulkResult.email_inviate} email di attivazione inviate.</p>
                : <p className="text-sm text-amber-600 font-semibold">⚠️ Invio email DISATTIVATO: gli account sono stati creati, attiva l'invio dalle Impostazioni per spedire le credenziali.</p>}
              {bulkResult.duplicati > 0 && (
                <div className="rounded-lg border-2 border-amber-200 bg-amber-50 p-3" data-testid="bulk-res-duplicati">
                  <div className="font-bold text-amber-800 mb-1">Email già usata da un altro account ({bulkResult.duplicati})</div>
                  <p className="text-xs text-amber-700 mb-2">Un account è univoco per email. Questi fornitori condividono l'email con un altro account già esistente, quindi non è stato possibile creare un account separato:</p>
                  <div className="max-h-36 overflow-y-auto text-sm space-y-1">
                    {(bulkResult.duplicati_dettaglio || []).map((d, i) => (
                      <div key={i} className="flex flex-col">
                        <span className="font-semibold text-slate-800">{d.ragione_sociale}</span>
                        <span className="text-xs text-slate-500">{d.email} · già usata da: {d.in_uso_da}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {bulkResult.senza_email > 0 && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="bulk-res-senza-email">
                  <div className="font-bold text-slate-700 mb-1">Senza email ({bulkResult.senza_email})</div>
                  <p className="text-xs text-slate-500">{(bulkResult.senza_email_nomi || []).join(", ")}</p>
                </div>
              )}
            </div>
          ) : bulkList === null ? (
            <div className="py-8 text-center text-slate-500">Caricamento...</div>
          ) : bulkList.length === 0 ? (
            <div className="py-6 text-center text-slate-500">Nessun fornitore da attivare: tutti quelli con email risultano già attivi, oppure mancano le email.</div>
          ) : (
            <>
              <p className="text-base text-slate-600">Seleziona i fornitori a cui creare l'account e inviare le credenziali. Deseleziona quelli che vuoi escludere.</p>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">{Object.values(bulkSel).filter(Boolean).length} selezionati su {bulkList.length}</span>
                <div className="flex gap-3">
                  <button className="text-brand font-semibold" onClick={() => { const s = {}; bulkList.forEach((f) => s[f.id] = true); setBulkSel(s); }}>Tutti</button>
                  <button className="text-slate-500 font-semibold" onClick={() => setBulkSel({})}>Nessuno</button>
                </div>
              </div>
              <div className="max-h-72 overflow-y-auto border rounded-lg divide-y" data-testid="bulk-fornitori-list">
                {bulkList.map((f) => (
                  <label key={f.id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-slate-50" data-testid={`bulk-row-${f.ragione_sociale}`}>
                    <input type="checkbox" checked={!!bulkSel[f.id]} onChange={(e) => setBulkSel((s) => ({ ...s, [f.id]: e.target.checked }))} className="w-5 h-5 accent-brand" />
                    <div className="min-w-0">
                      <div className="font-semibold truncate">{f.ragione_sociale}</div>
                      <div className="text-sm text-slate-500 truncate">{f.email}{f.has_account ? " · account da attivare" : ""}</div>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-400">Le email partono solo se l'interruttore "Invio credenziali" è attivo nelle Impostazioni; altrimenti gli account vengono creati e potrai inviarle in seguito.</p>
            </>
          )}
          <DialogFooter className="gap-2">
            {bulkResult ? (
              <Button data-testid="bulk-result-close" onClick={() => setBulkOpen(false)} className="h-11 bg-brand hover:bg-brand-dark font-bold">Ho capito</Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setBulkOpen(false)}>Annulla</Button>
                {bulkList && bulkList.length > 0 && (
                  <Button data-testid="bulk-invia-button" disabled={bulkBusy} onClick={inviaBulk} className="h-11 bg-brand hover:bg-brand-dark font-bold">
                    {bulkBusy ? "Invio..." : `Crea e invia (${Object.values(bulkSel).filter(Boolean).length})`}
                  </Button>
                )}
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CategorieManager open={catOpen} onOpenChange={setCatOpen} onChange={(lista) => setCats(lista)} />
      <FornitoreFascicoloDialog fornitore={fascicoloForn} onClose={() => setFascicoloForn(null)} />

      {/* Iter113 · Dialog conferma invio riepilogo attività */}
      <Dialog open={!!riepForn} onOpenChange={(v) => { if (!v && !riepInv) setRiepForn(null); }}>
        <DialogContent data-testid="riepilogo-dialog" className="max-w-md">
          <DialogHeader><DialogTitle className="text-xl flex items-center gap-2"><Send className="w-5 h-5 text-indigo-600" /> Invia riepilogo attività</DialogTitle></DialogHeader>
          {riepForn && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                Invia via email a <b>{riepForn.ragione_sociale}</b> (<span className="text-indigo-700">{riepForn.email}</span>) un riepilogo PDF delle attività in corso.
              </p>
              <div className="rounded-lg border-2 border-slate-200 p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="riepilogo-opt-manutenzioni" checked={riepOpts.manutenzioni}
                    onChange={(e) => setRiepOpts((o) => ({ ...o, manutenzioni: e.target.checked }))} className="w-4 h-4" />
                  <span className="text-sm font-semibold">Manutenzioni attive assegnate</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" data-testid="riepilogo-opt-preventivi" checked={riepOpts.preventivi}
                    onChange={(e) => setRiepOpts((o) => ({ ...o, preventivi: e.target.checked }))} className="w-4 h-4" />
                  <span className="text-sm font-semibold">Preventivi in corso</span>
                </label>
              </div>
              <p className="text-xs text-slate-500">Verranno inclusi solo i documenti in stato attivo/aperto (non chiusi né annullati).</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRiepForn(null)} disabled={riepInv} data-testid="riepilogo-cancel">Annulla</Button>
            <Button variant="outline" onClick={anteprimaRiepilogo} disabled={riepInv} data-testid="riepilogo-anteprima"
              className="border-brand/40 text-brand hover:bg-brand/5"
              title="Apre il riepilogo PDF in una nuova scheda per visionarlo o stamparlo, senza inviare email.">
              <Eye className="w-4 h-4 mr-1" /> Anteprima PDF
            </Button>
            <Button className="bg-brand hover:bg-brand-dark text-white" onClick={inviaRiepilogo} disabled={riepInv} data-testid="riepilogo-confirm">
              {riepInv ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Invio in corso...</> : <><Send className="w-4 h-4 mr-1" /> Invia</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, v, on, testid, required }) {
  return (
    <div>
      <Label className="text-sm font-semibold">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</Label>
      <Input data-testid={testid} value={v} onChange={(e) => on(e.target.value)} className="h-12 mt-1" />
    </div>
  );
}

// Iter122fh · Vista minimal: tabella compatta con checkbox, denominazione, categoria.
// Consente selezione multipla + raggruppamento per categoria + ordinamento A-Z / Z-A.
function MinimalToolbar({ list, selectedIds, setSelectedIds, groupBy, setGroupBy, sortDir, setSortDir, isPlatformAdmin, onExport }) {
  const allSelected = list.length > 0 && list.every((f) => selectedIds.has(f.id));
  const toggleAll = () => {
    const next = new Set(selectedIds);
    if (allSelected) list.forEach((f) => next.delete(f.id));
    else list.forEach((f) => next.add(f.id));
    setSelectedIds(next);
  };
  return (
    <div className="flex flex-wrap items-center gap-3 mb-3 p-3 bg-slate-50 border-2 border-slate-200 rounded-lg" data-testid="minimal-toolbar">
      <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 cursor-pointer">
        <input type="checkbox" data-testid="minimal-select-all" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-brand" />
        Seleziona tutti ({list.length})
      </label>
      <div className="inline-flex items-center gap-2">
        <label className="text-sm font-semibold text-slate-600">Raggruppa:</label>
        <select data-testid="minimal-groupby" value={groupBy} onChange={(e) => setGroupBy(e.target.value)}
          className="h-9 text-sm border-2 border-slate-300 rounded-lg px-2 bg-white font-semibold">
          <option value="nessuno">Nessuno</option>
          <option value="categoria">Per categoria</option>
        </select>
      </div>
      <div className="inline-flex items-center gap-2">
        <label className="text-sm font-semibold text-slate-600">Ordina:</label>
        <select data-testid="minimal-sortdir" value={sortDir} onChange={(e) => setSortDir(e.target.value)}
          className="h-9 text-sm border-2 border-slate-300 rounded-lg px-2 bg-white font-semibold">
          <option value="az">A → Z</option>
          <option value="za">Z → A</option>
        </select>
      </div>
      <div className="flex-1" />
      <span className="text-sm font-semibold text-slate-700" data-testid="minimal-selected-count">
        {selectedIds.size} selezionat{selectedIds.size === 1 ? "o" : "i"}
      </span>
      {isPlatformAdmin && (
        <>
          <Button data-testid="minimal-export-xlsx" onClick={() => onExport("xlsx")} disabled={selectedIds.size === 0}
            className="h-9 bg-brand hover:bg-brand-dark text-white text-sm font-bold">
            <Share2 className="w-4 h-4 mr-1.5" /> Esporta Excel selezione
          </Button>
          <Button data-testid="minimal-export-csv" onClick={() => onExport("csv")} disabled={selectedIds.size === 0}
            variant="outline" className="h-9 border-brand/40 text-brand text-sm font-bold">
            <Share2 className="w-4 h-4 mr-1.5" /> Esporta CSV selezione
          </Button>
        </>
      )}
    </div>
  );
}

function MinimalTable({ list, selectedIds, setSelectedIds, groupBy, sortDir, search }) {
  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelectedIds(next);
  };
  const sorted = [...list].sort((a, b) => {
    const na = (a.ragione_sociale || "").toLowerCase();
    const nb = (b.ragione_sociale || "").toLowerCase();
    return sortDir === "az" ? na.localeCompare(nb) : nb.localeCompare(na);
  });
  const rowsFor = (arr) => (
    <div className="divide-y divide-slate-100">
      {arr.map((f) => (
        <div key={f.id} data-testid={`minimal-row-${f.ragione_sociale}`} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50">
          <input type="checkbox" data-testid={`minimal-row-checkbox-${f.ragione_sociale}`}
            checked={selectedIds.has(f.id)} onChange={() => toggleOne(f.id)}
            className="w-4 h-4 accent-brand shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{highlightMatch(f.ragione_sociale, search)}</div>
          </div>
          <div className="flex flex-wrap gap-1 justify-end min-w-0">
            {(f.categorie || []).map((c) => (
              <span key={c} className="px-2 py-0.5 bg-slate-100 rounded text-xs font-semibold">{c}</span>
            ))}
            {!(f.categorie || []).length && <span className="text-xs italic text-slate-400">senza categoria</span>}
          </div>
        </div>
      ))}
    </div>
  );
  if (list.length === 0) {
    return <Card className="p-6 text-center text-slate-500">Nessun fornitore corrisponde al filtro selezionato.</Card>;
  }
  if (groupBy === "categoria") {
    const groups = new Map();
    sorted.forEach((f) => {
      const cats = (f.categorie && f.categorie.length) ? f.categorie : ["Senza categoria"];
      cats.forEach((c) => {
        if (!groups.has(c)) groups.set(c, []);
        groups.get(c).push(f);
      });
    });
    const gk = [...groups.keys()].sort((a, b) => sortDir === "az" ? a.localeCompare(b) : b.localeCompare(a));
    return (
      <div className="space-y-4">
        {gk.map((cat) => (
          <Card key={cat} className="border-2 overflow-hidden" data-testid={`minimal-group-${cat}`}>
            <div className="px-3 py-2 bg-brand/10 text-brand font-bold text-sm flex items-center justify-between">
              <span>{cat}</span>
              <span className="text-xs bg-white rounded-full px-2 py-0.5">{groups.get(cat).length} fornitor{groups.get(cat).length === 1 ? "e" : "i"}</span>
            </div>
            {rowsFor(groups.get(cat))}
          </Card>
        ))}
      </div>
    );
  }
  return (
    <Card className="border-2 overflow-hidden" data-testid="minimal-table">
      {rowsFor(sorted)}
    </Card>
  );
}
